import { useState } from "react";
import { Shell } from "@/components/layout/Shell";
import { useSeo } from "@/hooks/use-seo";
import { AdSlot } from "@/components/ads/AdSlot";
import { motion } from "framer-motion";
import { Copy, Check, Moon, Sun } from "lucide-react";
import { cn } from "@/lib/utils";

// ── Psychological presets ─────────────────────────────────────────────────────

const PRESETS = [
  { label: "Build Institutional Trust & Security",   bg: "#FFFFFF", surface: "#EFF6FF", primary: "#1D4ED8", text: "#1E293B" },
  { label: "Drive Impulse Sales & Urgency",           bg: "#FFF7ED", surface: "#FFFFFF", primary: "#EA580C", text: "#1C1917" },
  { label: "Biophilic Calm & Wellness",               bg: "#F0FDF4", surface: "#FFFFFF", primary: "#16A34A", text: "#14532D" },
  { label: "Modern Luxury & Elegance",                bg: "#0C0C0C", surface: "#1C1C1C", primary: "#C9A96E", text: "#F5EFE6" },
  { label: "Dopamine & Youthful Playfulness",         bg: "#FAFAFA", surface: "#FFFFFF", primary: "#EC4899", text: "#1F2937" },
  { label: "Cognitive Focus & Minimal Strain",        bg: "#FAFAFA", surface: "#FFFFFF", primary: "#4F46E5", text: "#1C1917" },
  { label: "Organic & Sustainable Authority",         bg: "#F8F5EF", surface: "#FFFEF7", primary: "#65A30D", text: "#1A2E05" },
  { label: "High-Contrast Accessibility Focus",       bg: "#FFFFFF", surface: "#F9FAFB", primary: "#111827", text: "#111827" },
] as const;

// ── Vision modes ──────────────────────────────────────────────────────────────

const VISION_MODES = [
  { id: "normal",       label: "Normal Vision",              filterId: null },
  { id: "protanopia",   label: "Protanopia (Red-Blind)",      filterId: "cmb-protanopia" },
  { id: "deuteranopia", label: "Deuteranopia (Green-Blind)",  filterId: "cmb-deuteranopia" },
  { id: "tritanopia",   label: "Tritanopia (Blue-Blind)",     filterId: "cmb-tritanopia" },
] as const;
type VisionMode = typeof VISION_MODES[number]["id"];

// ── Helpers ───────────────────────────────────────────────────────────────────

function hexToHSL(hex: string): { h: number; s: number; l: number } {
  const safeHex = /^#[0-9a-fA-F]{6}$/.test(hex) ? hex : "#888888";
  const r = parseInt(safeHex.slice(1, 3), 16) / 255;
  const g = parseInt(safeHex.slice(3, 5), 16) / 255;
  const b = parseInt(safeHex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l: Math.round(l * 100) };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = 0;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;
  return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
}

function getHslProfile(hex: string): string {
  const { h, s, l } = hexToHSL(hex);
  if (s < 10)
    return "This near-neutral hue sits outside the emotional activation zones of the spectrum, projecting restraint and intentional authority. Low-saturation palettes signal confidence without performance — the visual equivalent of speaking only when it counts.";
  if (l > 75)
    return "At high lightness, this hue shifts from stimulating to expansive — light tones are associated with openness, clarity, and reduced cognitive load. Pale palettes slow the perceived passage of time and lower physiological arousal, supporting focus and trust.";
  if (l < 25)
    return "Dark value pulls this hue into gravity and depth. Dark-valued palettes activate associations of premium quality, exclusivity, and authority — the perceptual weight of the colour amplifies perceived importance and commands sustained attention.";
  if (h < 30 || h > 330)
    return "Red-spectrum wavelengths are the fastest to reach the visual cortex and the most physiologically activating colour on the spectrum. They raise heart rate, accelerate time perception, and amplify urgency — powerful for action triggers, but requiring restraint to avoid visual fatigue.";
  if (h < 70)
    return "Orange and amber wavelengths combine red's urgency with yellow's optimism, landing at the warmest point of the spectrum. They are reliably associated with appetite, energy, social engagement, and approachability — highly effective for call-to-action elements and expressive brand marks.";
  if (h < 100)
    return "Yellow activates higher cognitive alertness than any other hue and is detected peripherally faster than any colour except red. It signals novelty and energy at low saturation, but tips into visual noise when overused — best deployed as an accent to direct attention without demanding it.";
  if (h < 160)
    return "Green is the most comfortable hue for the human visual system, falling at the peak sensitivity of the eye's cone photoreceptors. It consistently lowers cortisol, reduces eye strain, and communicates life, safety, and permission — which is why it dominates health, finance, and sustainability branding globally.";
  if (h < 200)
    return "Teal and cyan wavelengths activate both the emotional warmth of green and the cognitive trust of blue simultaneously. This makes the hue exceptionally versatile — it reads as both approachable and competent, strongly associated with clarity, innovation, and refreshing honesty.";
  if (h < 260)
    return "Blue is the world's most universally trusted colour, dominant across every culture in branding for financial institutions, healthcare, and government. It reduces perceived temperature, slows time perception, and activates analytical thinking — optimal wherever credibility and focus are the primary values to communicate.";
  if (h < 300)
    return "Violet and purple occupy the shortest visible wavelengths the human eye can process, historically the most expensive pigments to produce. They carry deep associations with transformation, creative authority, and spiritual depth — and rank highest in research on perceived originality and luxury.";
  return "Pink and rose hues activate associations of intimacy, care, and emotional safety. Research shows pink-adjacent palettes lower aggression and increase the perception of sincerity — among the most effective colours for brands built on genuine human connection.";
}

function getLuminance(hex: string): number {
  const safeHex = /^#[0-9a-fA-F]{6}$/.test(hex) ? hex : "#888888";
  const r = parseInt(safeHex.slice(1, 3), 16) / 255;
  const g = parseInt(safeHex.slice(3, 5), 16) / 255;
  const b = parseInt(safeHex.slice(5, 7), 16) / 255;
  const lin = (c: number) => (c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

function getContrastRatio(fg: string, bg: string): number {
  const l1 = getLuminance(fg), l2 = getLuminance(bg);
  return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
}

// ── Canvas tab components ─────────────────────────────────────────────────────

interface CP { bg: string; surface: string; primary: string; text: string }

function SaasView({ p }: { p: CP }) {
  return (
    <div className="rounded-2xl overflow-hidden border border-black/10" style={{ backgroundColor: p.bg }}>
      <div className="px-5 py-3 flex items-center justify-between" style={{ backgroundColor: p.surface, borderBottom: `1px solid ${p.text}10` }}>
        <span className="text-sm font-semibold" style={{ color: p.text }}>mychromamind</span>
        <div className="flex items-center gap-3">
          {["Features", "Pricing", "Docs"].map((lbl) => (
            <span key={lbl} className="text-xs opacity-50 hidden sm:block" style={{ color: p.text }}>{lbl}</span>
          ))}
          <span className="text-[11px] px-3 py-1 rounded-full font-semibold" style={{ backgroundColor: p.primary, color: p.bg }}>
            Get started
          </span>
        </div>
      </div>
      <div className="px-6 py-10 text-center">
        <span
          className="inline-block px-3 py-0.5 rounded-full text-[11px] font-medium mb-4 border"
          style={{ borderColor: p.primary, color: p.primary }}
        >
          Color Psychology Engine
        </span>
        <h2 className="text-2xl font-bold mb-3 leading-tight" style={{ color: p.text }}>
          Turn any color into<br />a brand strategy
        </h2>
        <p className="text-sm max-w-xs mx-auto mb-6 leading-relaxed" style={{ color: p.text, opacity: 0.65 }}>
          Real-time palette testing grounded in color psychology research. See your colors across real UI components instantly.
        </p>
        <button
          className="px-6 py-2.5 rounded-full text-sm font-semibold shadow-sm"
          style={{ backgroundColor: p.primary, color: p.bg }}
        >
          Start for free →
        </button>
      </div>
    </div>
  );
}

function EcomView({ p }: { p: CP }) {
  return (
    <div className="max-w-xs mx-auto rounded-2xl overflow-hidden border border-black/10" style={{ backgroundColor: p.surface }}>
      <div className="h-44 relative flex items-center justify-center" style={{ backgroundColor: p.bg }}>
        <svg width="48" height="48" viewBox="0 0 48 48" fill="none" style={{ opacity: 0.12, color: p.text }}>
          <rect x="4" y="12" width="40" height="28" rx="3" stroke="currentColor" strokeWidth="2"/>
          <circle cx="17" cy="22" r="5" stroke="currentColor" strokeWidth="2"/>
          <path d="M4 32L14 22L22 30L32 19L44 32" stroke="currentColor" strokeWidth="2"/>
        </svg>
        <span className="absolute top-3 left-3 text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: p.primary, color: p.bg }}>
          SALE
        </span>
      </div>
      <div className="p-4">
        <p className="text-[11px] mb-1" style={{ color: p.text, opacity: 0.5 }}>mychromamind Studio</p>
        <h3 className="text-sm font-semibold mb-2" style={{ color: p.text }}>Color Psychology Toolkit</h3>
        <div className="flex items-baseline gap-2 mb-4">
          <span className="text-lg font-bold" style={{ color: p.primary }}>$49</span>
          <span className="text-xs line-through" style={{ color: p.text, opacity: 0.4 }}>$79</span>
          <span className="text-[10px] px-1.5 py-0.5 rounded font-medium" style={{ backgroundColor: p.primary + "22", color: p.primary }}>
            38% off
          </span>
        </div>
        <button className="w-full py-2.5 rounded-xl text-sm font-semibold" style={{ backgroundColor: p.primary, color: p.bg }}>
          Add to Cart
        </button>
      </div>
    </div>
  );
}

function MobileView({ p }: { p: CP }) {
  const bars = [40, 65, 50, 80, 70, 95, 75];
  return (
    <div className="flex justify-center">
      <div
        className="w-52 rounded-[2.2rem] border-4 overflow-hidden shadow-xl"
        style={{ borderColor: p.text + "18", backgroundColor: p.bg }}
      >
        <div className="h-6 flex items-center justify-between px-5 pt-1" style={{ backgroundColor: p.surface }}>
          <span className="text-[9px] font-medium" style={{ color: p.text, opacity: 0.55 }}>9:41</span>
          <div className="w-12 h-2.5 rounded-full" style={{ backgroundColor: p.text + "18" }} />
          <span className="text-[9px] font-medium" style={{ color: p.text, opacity: 0.55 }}>100%</span>
        </div>
        <div className="px-3 pb-5 pt-3" style={{ backgroundColor: p.bg }}>
          <p className="text-[10px] font-medium mb-0.5" style={{ color: p.text, opacity: 0.45 }}>Dashboard</p>
          <h3 className="text-sm font-bold mb-3" style={{ color: p.text }}>Color Metrics</h3>
          <div className="grid grid-cols-2 gap-1.5 mb-2.5">
            {[
              { label: "Palette Score", val: "94", accent: true },
              { label: "WCAG Contrast", val: "AA",  accent: false },
              { label: "Trust Index",   val: "87%", accent: true },
              { label: "Mood Align",    val: "High", accent: false },
            ].map((m) => (
              <div key={m.label} className="rounded-xl p-2.5" style={{ backgroundColor: p.surface }}>
                <p className="text-[8px] mb-0.5" style={{ color: p.text, opacity: 0.45 }}>{m.label}</p>
                <p className="text-sm font-bold" style={{ color: m.accent ? p.primary : p.text }}>{m.val}</p>
              </div>
            ))}
          </div>
          <div className="rounded-xl p-2.5" style={{ backgroundColor: p.surface }}>
            <p className="text-[8px] mb-1.5" style={{ color: p.text, opacity: 0.45 }}>Weekly Trend</p>
            <div className="flex items-end gap-0.5 h-8">
              {bars.map((h, i) => (
                <div
                  key={i}
                  className="flex-1 rounded-t"
                  style={{ height: `${h}%`, backgroundColor: i === 5 ? p.primary : p.primary + "44" }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function BlogView({ p }: { p: CP }) {
  return (
    <div
      className="rounded-2xl border border-black/10 p-6 sm:p-8 max-w-lg mx-auto"
      style={{ backgroundColor: p.bg }}
    >
      <p className="text-[10px] uppercase tracking-widest mb-2" style={{ color: p.text, opacity: 0.45 }}>
        Color Psychology · 5 min read
      </p>
      <h2 className="text-xl font-bold mb-3 leading-snug" style={{ color: p.text }}>
        Why Your Color Choices Reveal More Than You Think
      </h2>
      <div className="flex items-center gap-2.5 mb-5 pb-4" style={{ borderBottom: `1px solid ${p.text}12` }}>
        <div className="w-7 h-7 rounded-full" style={{ backgroundColor: p.primary }} />
        <div>
          <p className="text-[11px] font-medium" style={{ color: p.text }}>Mychromamind Editorial</p>
          <p className="text-[10px]" style={{ color: p.text, opacity: 0.45 }}>July 30, 2026</p>
        </div>
      </div>
      <p className="text-sm leading-relaxed mb-4" style={{ color: p.text }}>
        <span
          className="float-left mr-1 text-[3.4rem] leading-none font-serif font-bold"
          style={{ color: p.primary }}
        >
          C
        </span>
        olor is never neutral. Every palette decision carries embedded signals — physiological responses
        that fire before conscious thought catches up. What you choose to surround yourself with is a
        form of self-report, often more honest than anything said aloud.
      </p>
      <blockquote
        className="border-l-4 pl-4 py-1 text-sm italic"
        style={{ borderColor: p.primary, color: p.text, opacity: 0.75 }}
      >
        "The colors we are drawn to reveal the emotional state we're navigating — not the one we're performing."
      </blockquote>
    </div>
  );
}

// ── Sidebar section wrapper ───────────────────────────────────────────────────

function SideSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4 space-y-3">
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium">{title}</p>
      {children}
    </div>
  );
}

// ── Color picker row ──────────────────────────────────────────────────────────

function ColorRow({
  label, value, onChange,
}: { label: string; value: string; onChange: (v: string) => void }) {
  const valid = /^#[0-9a-fA-F]{6}$/.test(value);
  return (
    <label className="flex items-center gap-2 cursor-pointer group">
      <input
        type="color"
        value={valid ? value : "#888888"}
        onChange={(e) => onChange(e.target.value)}
        className="w-7 h-7 rounded-lg border border-border cursor-pointer bg-transparent p-0.5"
      />
      <span className="text-xs text-muted-foreground w-16 shrink-0">{label}</span>
      <input
        type="text"
        value={value}
        maxLength={7}
        onChange={(e) => {
          const v = e.target.value.startsWith("#") ? e.target.value : "#" + e.target.value;
          onChange(v);
        }}
        className="flex-1 text-xs font-mono bg-muted/60 border border-border rounded-md px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary/50 min-w-0"
      />
    </label>
  );
}

// ── Copy button ───────────────────────────────────────────────────────────────

function CopyButton({ label, onClick, copied }: { label: string; onClick: () => void; copied: boolean }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "flex items-center gap-2 w-full px-3 py-2 rounded-xl border text-xs font-medium transition-all",
        copied
          ? "border-emerald-500/60 text-emerald-600 bg-emerald-50 dark:bg-emerald-950/30"
          : "border-border hover:border-primary/40 hover:text-primary"
      )}
    >
      {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
      {copied ? "Copied!" : label}
    </button>
  );
}

// ── Tabs ──────────────────────────────────────────────────────────────────────

type Tab = "saas" | "ecom" | "mobile" | "blog";
const TABS: { id: Tab; label: string }[] = [
  { id: "saas",   label: "SaaS Landing" },
  { id: "ecom",   label: "Product Card" },
  { id: "mobile", label: "Mobile App" },
  { id: "blog",   label: "Blog Article" },
];

// ── Page ──────────────────────────────────────────────────────────────────────

export default function ColorSandbox() {
  useSeo({
    title: "Color Psychology Sandbox — Live UI Builder & Palette Tester | Mychromamind",
    description:
      "Test any color palette across real UI components — SaaS landing page, e-commerce card, mobile app, and editorial blog. Includes live WCAG contrast checker, color-blindness simulator, psychological profile engine, and one-click CSS/Tailwind export. Free, instant, browser-only.",
    path: "/color-sandbox",
    structuredData: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebApplication",
          name: "Color Psychology Sandbox",
          applicationCategory: "DesignApplication",
          operatingSystem: "Any (Web)",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
          description:
            "A real-time color palette testing environment with psychological intent presets, WCAG contrast auditor, color-blindness vision simulator, and CSS/Tailwind export — grounded in color psychology research.",
          url: "https://mychromamind.com/color-sandbox",
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Mychromamind", item: "https://mychromamind.com/" },
            { "@type": "ListItem", position: 2, name: "Color Psychology Sandbox", item: "https://mychromamind.com/color-sandbox" },
          ],
        },
      ],
    },
  });

  const [tab, setTab]     = useState<Tab>("saas");
  const [vision, setVision] = useState<VisionMode>("normal");
  const [darkMode, setDarkMode] = useState(false);
  const [copiedCSS, setCopiedCSS]         = useState(false);
  const [copiedTailwind, setCopiedTailwind] = useState(false);
  const [palette, setPalette] = useState({
    bg:      "#FFFFFF",
    surface: "#F8F9FA",
    primary: "#6366F1",
    text:    "#111827",
  });

  // Dark mode override merges with palette (preserves primary accent)
  const p = darkMode
    ? { ...palette, bg: "#111827", surface: "#1F2937", text: "#F9FAFB" }
    : palette;

  const contrastRatio = getContrastRatio(p.text, p.surface);
  const contrastPass  = contrastRatio >= 4.5;
  const hslProfile    = getHslProfile(p.primary);
  const { h, s, l }   = hexToHSL(p.primary);
  const visionFilter  = VISION_MODES.find((v) => v.id === vision)?.filterId ?? null;

  function applyPreset(idx: number) {
    const pr = PRESETS[idx];
    setPalette({ bg: pr.bg, surface: pr.surface, primary: pr.primary, text: pr.text });
    setDarkMode(false);
  }

  function copyCSS() {
    const css = `:root {\n  /* --mychromamind-palette */\n  --color-bg: ${p.bg};\n  --color-surface: ${p.surface};\n  --color-primary: ${p.primary};\n  --color-text: ${p.text};\n}`;
    navigator.clipboard.writeText(css).catch(() => null);
    setCopiedCSS(true);
    setTimeout(() => setCopiedCSS(false), 2000);
  }

  function copyTailwind() {
    const tw = `// tailwind.config.js — --mychromamind-palette\nmodule.exports = {\n  theme: {\n    extend: {\n      colors: {\n        background: "${p.bg}",\n        surface: "${p.surface}",\n        primary: "${p.primary}",\n        foreground: "${p.text}",\n      },\n    },\n  },\n};`;
    navigator.clipboard.writeText(tw).catch(() => null);
    setCopiedTailwind(true);
    setTimeout(() => setCopiedTailwind(false), 2000);
  }

  return (
    <Shell>
      {/* Inline SVG filter definitions for color-blindness simulation */}
      <svg aria-hidden="true" className="absolute w-0 h-0 overflow-hidden" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <filter id="cmb-protanopia">
            <feColorMatrix type="matrix" values="0.567 0.433 0 0 0  0.558 0.442 0 0 0  0 0.242 0.758 0 0  0 0 0 1 0" />
          </filter>
          <filter id="cmb-deuteranopia">
            <feColorMatrix type="matrix" values="0.625 0.375 0 0 0  0.7 0.3 0 0 0  0 0.3 0.7 0 0  0 0 0 1 0" />
          </filter>
          <filter id="cmb-tritanopia">
            <feColorMatrix type="matrix" values="0.95 0.05 0 0 0  0 0.433 0.567 0 0  0 0.475 0.525 0 0  0 0 0 1 0" />
          </filter>
        </defs>
      </svg>

      <div className="flex-1 px-4 sm:px-6 py-8 max-w-6xl mx-auto w-full">
        {/* Header */}
        <div className="mb-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-muted text-xs font-medium tracking-wide uppercase text-foreground mb-3">
            <span className="w-2 h-2 rounded-full bg-primary" />
            Color Psychology Sandbox
          </div>
          <h1 className="text-2xl sm:text-4xl font-serif leading-tight mb-2">
            Live UI Builder &amp; Palette Tester
          </h1>
          <p className="text-sm text-muted-foreground max-w-2xl">
            Pick a psychological intent preset or build your own palette — then see it rendered live across real UI components with instant WCAG contrast checking and color-blindness simulation.
          </p>
        </div>

        <AdSlot slotId="2431849490" content={[hslProfile]} className="mb-6" />

        {/* Two-column layout */}
        <div className="flex flex-col lg:flex-row gap-5">

          {/* ── Sidebar ── */}
          <aside className="lg:w-72 xl:w-80 shrink-0 space-y-3">

            {/* Palette pickers */}
            <SideSection title="Palette Colors">
              <ColorRow label="Background" value={p.bg}      onChange={(v) => { setDarkMode(false); setPalette((prev) => ({ ...prev, bg: v })); }} />
              <ColorRow label="Surface"    value={p.surface} onChange={(v) => { setDarkMode(false); setPalette((prev) => ({ ...prev, surface: v })); }} />
              <ColorRow label="Primary"    value={p.primary} onChange={(v) => setPalette((prev) => ({ ...prev, primary: v }))} />
              <ColorRow label="Text"       value={p.text}    onChange={(v) => { setDarkMode(false); setPalette((prev) => ({ ...prev, text: v })); }} />
              <button
                onClick={() => setDarkMode((d) => !d)}
                className="flex items-center gap-2 w-full px-3 py-2 rounded-xl border border-border hover:border-primary/40 text-xs font-medium transition-colors"
              >
                {darkMode ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
                {darkMode ? "Restore light mode" : "Flip to dark mode"}
              </button>
            </SideSection>

            {/* Psychological intent presets */}
            <SideSection title="Psychological Intent">
              <select
                onChange={(e) => applyPreset(Number(e.target.value))}
                defaultValue=""
                className="w-full text-xs bg-muted/60 border border-border rounded-xl px-3 py-2 focus:outline-none focus:ring-1 focus:ring-primary/50"
              >
                <option value="" disabled>Select a goal…</option>
                {PRESETS.map((pr, i) => (
                  <option key={i} value={i}>{pr.label}</option>
                ))}
              </select>
            </SideSection>

            {/* Vision lens */}
            <SideSection title="Vision Lens">
              <select
                value={vision}
                onChange={(e) => setVision(e.target.value as VisionMode)}
                className="w-full text-xs bg-muted/60 border border-border rounded-xl px-3 py-2 focus:outline-none focus:ring-1 focus:ring-primary/50"
              >
                {VISION_MODES.map((vm) => (
                  <option key={vm.id} value={vm.id}>{vm.label}</option>
                ))}
              </select>
              {vision !== "normal" && (
                <p className="text-[10px] text-muted-foreground leading-relaxed">
                  Canvas below is rendered through a simulated {VISION_MODES.find((v) => v.id === vision)?.label.toLowerCase()} filter.
                </p>
              )}
            </SideSection>

            {/* HSL Profile */}
            <SideSection title="Psychological Profile">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-5 h-5 rounded-full ring-1 ring-border" style={{ backgroundColor: p.primary }} />
                <span className="text-[11px] font-mono text-muted-foreground">{p.primary} · H{h} S{s} L{l}</span>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">{hslProfile}</p>
            </SideSection>

            {/* WCAG Auditor */}
            <SideSection title="WCAG 2.1 Contrast Auditor">
              <div className={cn(
                "flex items-center justify-between px-3 py-2.5 rounded-xl text-xs font-semibold",
                contrastPass ? "bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700" : "bg-red-50 dark:bg-red-950/30 text-red-600"
              )}>
                <span>{contrastPass ? "✓ PASS — WCAG AA" : "✗ FAIL — Low Contrast"}</span>
                <span>{contrastRatio.toFixed(2)}:1</span>
              </div>
              <p className="text-[10px] text-muted-foreground">Text vs. Surface · AA requires ≥ 4.5:1</p>
            </SideSection>

            {/* Export */}
            <SideSection title="Export Configuration">
              <CopyButton label="Copy CSS Variables"  onClick={copyCSS}      copied={copiedCSS} />
              <CopyButton label="Copy Tailwind Config" onClick={copyTailwind} copied={copiedTailwind} />
            </SideSection>

          </aside>

          {/* ── Canvas ── */}
          <div className="flex-1 min-w-0 space-y-3">
            {/* Tab switcher */}
            <div className="flex gap-1.5 p-1 rounded-xl border border-border bg-muted/40 w-fit">
              {TABS.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={cn(
                    "px-3 py-1.5 rounded-lg text-xs font-medium transition-all",
                    tab === t.id
                      ? "bg-background shadow-sm text-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {/* Live canvas with vision filter */}
            <div
              style={visionFilter ? { filter: `url(#${visionFilter})` } : undefined}
              className="w-full"
            >
              <motion.div
                key={tab}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25 }}
              >
                {tab === "saas"   && <SaasView   p={p} />}
                {tab === "ecom"   && <EcomView   p={p} />}
                {tab === "mobile" && <MobileView p={p} />}
                {tab === "blog"   && <BlogView   p={p} />}
              </motion.div>
            </div>

            {/* Palette swatches summary */}
            <div className="flex items-center gap-2 pt-1">
              {[
                { label: "BG",      hex: p.bg },
                { label: "Surface", hex: p.surface },
                { label: "Primary", hex: p.primary },
                { label: "Text",    hex: p.text },
              ].map((sw) => (
                <div key={sw.label} className="flex items-center gap-1.5">
                  <span
                    className="w-5 h-5 rounded-md ring-1 ring-border"
                    style={{ backgroundColor: sw.hex }}
                  />
                  <span className="text-[10px] text-muted-foreground hidden sm:block">{sw.label}</span>
                </div>
              ))}
              {vision !== "normal" && (
                <span className="ml-auto text-[10px] text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                  Viewing as: {VISION_MODES.find((v) => v.id === vision)?.label}
                </span>
              )}
            </div>
          </div>
        </div>

        <AdSlot slotId="2431849490" content={[hslProfile]} className="mt-8" />

        <p className="text-xs text-muted-foreground text-center mt-6 max-w-md mx-auto">
          For design exploration and education only. Color blindness filters are approximate simulations — test with dedicated accessibility tools before shipping.
        </p>
      </div>
    </Shell>
  );
}
